import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TopBar from "../components/TopBar";
import {
  colors,
  fonts,
  fontSizes,
  headingStyle,
  bodyTextStyle,
  primaryButton,
} from "../theme";

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to detect mobile breakpoint using matchMedia.
 * Returns true when viewport is <= 720px.
 */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    // Initialize from matchMedia if available (SSR-safe)
    if (typeof window !== "undefined") {
      return window.matchMedia("(max-width: 720px)").matches;
    }
    return false;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 720px)");

    const handleChange = (e) => {
      setIsMobile(e.matches);
    };

    // Listen for changes
    mediaQuery.addEventListener("change", handleChange);

    // Cleanup listener on unmount
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return isMobile;
}

// ============================================================================
// DATA
// ============================================================================

const PROJECTS = [
  {
    id: "cyclistic-tableau",
    title: "Cyclistic Bike-Share Analysis",
    tool: "Tableau",
    featured: true,
    summary:
      "How do annual members and casual riders use Cyclistic bikes differently? This project analyzes 12 months of trip data to identify usage patterns and inform a marketing strategy to convert casual riders into annual members.",
    links: {
      dashboardUrl: "", // Placeholder - will add Tableau Public embed later
    },
  },
  {
    id: "looker-studio-placeholder",
    title: "Marketing Campaign Performance",
    tool: "Looker Studio",
    featured: false,
    summary:
      "A comprehensive dashboard tracking campaign ROI, channel performance, and audience engagement metrics. Built to help stakeholders make data-driven decisions on budget allocation and creative strategy.",
    links: {},
  },
];

export default function Portfolio() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeProjectId, setActiveProjectId] = useState(null);
  const isMobile = useIsMobile();
  
  // Tableau embed state
  const tableauMountRef = useRef(null);
  const timeoutRef = useRef(null);
  const [tableauScriptLoaded, setTableauScriptLoaded] = useState(false);
  const [vizLoading, setVizLoading] = useState(false);
  const [embedError, setEmbedError] = useState(null);

  // Initialize active project from URL query param or featured project
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const projectIdFromUrl = params.get("project");

    if (projectIdFromUrl) {
      // Validate the id exists
      const projectExists = PROJECTS.find((p) => p.id === projectIdFromUrl);
      if (projectExists) {
        setActiveProjectId(projectIdFromUrl);
      } else {
        // Invalid id - fallback to featured
        const featured = PROJECTS.find((p) => p.featured);
        setActiveProjectId(featured?.id || PROJECTS[0].id);
      }
    } else {
      // No query param - use featured or first project
      const featured = PROJECTS.find((p) => p.featured);
      setActiveProjectId(featured?.id || PROJECTS[0].id);
    }
  }, [location.search]);

  // Load Tableau JS API script once
  useEffect(() => {
    const scriptSrc = "https://public.tableau.com/javascripts/api/viz_v1.js";
    
    // Check if script already exists
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
    
    if (existingScript) {
      // Script already loaded
      setTableauScriptLoaded(true);
      return;
    }
    
    // Inject script
    const script = document.createElement("script");
    script.src = scriptSrc;
    script.async = true;
    script.onload = () => {
      setTableauScriptLoaded(true);
    };
    script.onerror = () => {
      console.error("Failed to load Tableau JS API");
      setEmbedError("Failed to load Tableau library");
    };
    
    document.head.appendChild(script);
  }, []);

  // Initialize/dispose Tableau embed based on active project
  useEffect(() => {
    const isCyclisticProject = activeProjectId === "cyclistic-tableau";
    
    // Cleanup function
    const cleanup = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (tableauMountRef.current) {
        tableauMountRef.current.innerHTML = "";
      }
      setVizLoading(false);
    };
    
    // If not Cyclistic project, cleanup and exit
    if (!isCyclisticProject) {
      cleanup();
      setEmbedError(null);
      return;
    }
    
    // If Cyclistic project but script not loaded yet, wait
    if (!tableauScriptLoaded) {
      return;
    }
    
    // Initialize embed for Cyclistic project
    if (tableauMountRef.current) {
      setVizLoading(true);
      setEmbedError(null);
      
      // Clear any existing content
      cleanup();
      
      const mount = tableauMountRef.current;
      
      // Wait for container to be laid out
      requestAnimationFrame(() => {
        if (!mount) return;
        
        console.log("Tableau embed: container dimensions", {
          width: mount.offsetWidth,
          height: mount.offsetHeight,
        });
        
        // Create placeholder div
        const placeholderDiv = document.createElement("div");
        placeholderDiv.style.position = "absolute";
        placeholderDiv.style.inset = "0";
        placeholderDiv.style.width = "100%";
        placeholderDiv.style.height = "100%";
        
        // Create object element
        const object = document.createElement("object");
        object.className = "tableauViz";
        object.style.width = "100%";
        object.style.height = "100%";
        
        // Create params
        const params = {
          "host_url": "https%3A%2F%2Fpublic.tableau.com%2F",
          "embed_code_version": "3",
          "site_root": "",
          "name": "GoogleDataAnalystCertificateCapstone-Cyclistic/Dashboard1",
          "tabs": "no",
          "toolbar": "yes",
          "static_image": "https://public.tableau.com/static/images/Go/GoogleDataAnalystCertificateCapstone-Cyclistic/Dashboard1/1.png",
          "animate_transition": "yes",
          "display_static_image": "yes",
          "display_spinner": "yes",
          "display_overlay": "yes",
          "display_count": "yes",
          "language": "en-US",
        };
        
        // Append params to object
        Object.entries(params).forEach(([name, value]) => {
          const param = document.createElement("param");
          param.setAttribute("name", name);
          param.setAttribute("value", value);
          object.appendChild(param);
        });
        
        // Assemble DOM structure
        placeholderDiv.appendChild(object);
        mount.appendChild(placeholderDiv);
        
        console.log("Tableau embed: object element inserted");
        
        // Check for iframe appearance
        const checkIframe = () => {
          const iframe = placeholderDiv.querySelector("iframe");
          if (iframe && mount.offsetWidth > 0 && mount.offsetHeight > 0) {
            console.log("Tableau embed: iframe detected");
            setVizLoading(false);
            setEmbedError(null);
            return true;
          }
          return false;
        };
        
        // Check immediately after a delay
        setTimeout(() => {
          if (!checkIframe()) {
            // Check again after another delay
            setTimeout(() => {
              if (!checkIframe()) {
                // Still no iframe, keep checking but set longer timeout
                const intervalId = setInterval(() => {
                  if (checkIframe()) {
                    clearInterval(intervalId);
                  }
                }, 500);
                
                // Final timeout after 8 seconds
                timeoutRef.current = setTimeout(() => {
                  clearInterval(intervalId);
                  if (!checkIframe()) {
                    console.error("Tableau embed: timeout waiting for iframe");
                    setEmbedError("Dashboard failed to load. Try refreshing, or open in Tableau Public.");
                    setVizLoading(false);
                  }
                }, 8000);
              }
            }, 1000);
          }
        }, 500);
      });
    }
    
    // Cleanup on unmount or project change
    return cleanup;
  }, [activeProjectId, tableauScriptLoaded]);

  const activeProject = PROJECTS.find((p) => p.id === activeProjectId);

  function handleViewProject(projectId) {
    setActiveProjectId(projectId);
    navigate(`?project=${projectId}`, { replace: false });
  }

  if (!activeProject) {
    return (
      <div style={styles.page}>
        <TopBar />
        <div style={styles.shell}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <TopBar />
      <div style={styles.shell}>
        {/* Page header */}
        <header style={styles.header}>
          <h1 style={styles.h1}>Portfolio</h1>
          <p style={styles.lede}>
            The Spotlight below showcases a selected project in detail. Browse
            the Gallery to explore more work and switch between projects.
          </p>
        </header>

        {/* Content */}
        <main style={styles.main}>
          {/* Section A: Spotlight (Project Viewer) */}
          <section style={styles.spotlightSection}>
            <div style={styles.spotlightCard}>
              <div style={styles.spotlightHeader}>
                <h2 style={styles.h2}>{activeProject.title}</h2>
                <span style={styles.toolTag}>{activeProject.tool}</span>
              </div>

              <p style={styles.spotlightSummary}>{activeProject.summary}</p>

              {/* Media area: Tableau embed or placeholder */}
              {activeProject.id === "cyclistic-tableau" ? (
                <div style={styles.mediaFrame}>
                  {embedError ? (
                    <div style={styles.errorPanel}>
                      <p style={styles.errorText}>{embedError}</p>
                      <a
                        href="https://public.tableau.com/views/GoogleDataAnalystCertificateCapstone-Cyclistic/Dashboard1"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.externalLink}
                      >
                        Open in Tableau Public →
                      </a>
                    </div>
                  ) : (
                    <>
                      {(vizLoading || !tableauScriptLoaded) && (
                        <div style={styles.loadingOverlay}>
                          <p style={styles.loadingText}>Loading dashboard…</p>
                        </div>
                      )}
                      <div style={styles.ratioBox}>
                        <div
                          ref={tableauMountRef}
                          style={styles.ratioFill}
                        />
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div style={styles.mediaPlaceholder}>
                  <p style={styles.mediaPlaceholderText}>
                    Interactive dashboard will appear here.
                  </p>
                </div>
              )}

              {/* External link if available */}
              {activeProject.links?.dashboardUrl && (
                <div style={styles.spotlightFooter}>
                  <a
                    href={activeProject.links.dashboardUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.externalLink}
                  >
                    Open on Tableau Public →
                  </a>
                </div>
              )}
            </div>
          </section>

          {/* Section B: Gallery (Project Selector) */}
          <section style={styles.gallerySection}>
            <h3 style={styles.galleryTitle}>Gallery</h3>
            <div
              style={
                isMobile ? styles.galleryCarouselMobile : styles.galleryCarousel
              }
            >
              {PROJECTS.map((project) => {
                const isActive = project.id === activeProjectId;
                return (
                  <GalleryCard
                    key={project.id}
                    project={project}
                    isActive={isActive}
                    onView={() => handleViewProject(project.id)}
                  />
                );
              })}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

function GalleryCard({ project, isActive, onView }) {
  return (
    <article
      style={{
        ...styles.galleryCard,
        ...(isActive ? styles.galleryCardActive : {}),
      }}
    >
      <div style={styles.galleryCardTop}>
        <h4 style={styles.h4}>{project.title}</h4>
        <span style={styles.galleryTag}>{project.tool}</span>
      </div>

      <p style={styles.galleryCardSummary}>{project.summary}</p>

      <button
        onClick={onView}
        disabled={isActive}
        style={{
          ...styles.viewButton,
          ...(isActive ? styles.viewButtonDisabled : {}),
        }}
      >
        {isActive ? "Viewing" : "View"}
      </button>
    </article>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = {
  // Page wrapper
  page: {
    minHeight: "100vh",
    backgroundColor: colors.bgPrimary,
  },

  // Content shell
  shell: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "40px 24px 80px",
  },

  // Page header
  header: {
    marginBottom: "48px",
  },

  h1: {
    ...headingStyle,
    fontSize: fontSizes.h1,
    marginBottom: "16px",
  },

  lede: {
    ...bodyTextStyle,
    fontSize: fontSizes.body,
    color: colors.textPrimary,
    maxWidth: "1200px",
    lineHeight: 1.6,
  },

  // Main content area
  main: {
    display: "flex",
    flexDirection: "column",
    gap: "48px",
  },

  // =========================================================================
  // SPOTLIGHT SECTION
  // =========================================================================

  spotlightSection: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "48px",
  },

  spotlightCard: {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    padding: "32px",
  },

  spotlightHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
    flexWrap: "wrap",
  },

  h2: {
    ...headingStyle,
    fontSize: fontSizes.h2,
    margin: 0,
  },

  toolTag: {
    backgroundColor: colors.accentPrimary,
    color: colors.white,
    padding: "4px 12px",
    borderRadius: "12px",
    fontSize: fontSizes.small,
    fontFamily: fonts.body,
    fontWeight: 600,
  },

  spotlightSummary: {
    ...bodyTextStyle,
    fontSize: fontSizes.body,
    color: colors.textPrimary,
    marginBottom: "24px",
    lineHeight: 1.6,
  },

  mediaPlaceholder: {
    backgroundColor: colors.surfaceAlt,
    border: `2px dashed ${colors.border}`,
    borderRadius: "8px",
    padding: "80px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "24px",
  },

  mediaPlaceholderText: {
    ...bodyTextStyle,
    fontSize: fontSizes.body,
    color: colors.textPrimary,
    opacity: 0.6,
  },

  // Tableau embed frame
  mediaFrame: {
    position: "relative",
    width: "100%",
    marginBottom: "24px",
    borderRadius: "8px",
    overflow: "hidden",
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
  },

  // Ratio box pattern (paddingTop creates 16:10 aspect ratio)
  ratioBox: {
    position: "relative",
    width: "100%",
    paddingTop: "62.5%",
  },

  // Fill container for viz
  ratioFill: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },

  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.surfaceAlt,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    borderRadius: "8px",
  },

  loadingText: {
    ...bodyTextStyle,
    fontSize: fontSizes.body,
    color: colors.textPrimary,
  },

  errorPanel: {
    padding: "80px 24px",
    backgroundColor: colors.surfaceAlt,
    border: `2px solid ${colors.border}`,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
  },

  errorText: {
    ...bodyTextStyle,
    fontSize: fontSizes.body,
    color: colors.textPrimary,
    textAlign: "center",
  },

  spotlightFooter: {
    display: "flex",
    justifyContent: "flex-end",
  },

  externalLink: {
    ...bodyTextStyle,
    fontSize: fontSizes.body,
    color: colors.accentPrimary,
    textDecoration: "none",
    fontWeight: 600,
    cursor: "pointer",
  },

  // =========================================================================
  // GALLERY SECTION
  // =========================================================================

  gallerySection: {
    display: "flex",
    flexDirection: "column",
  },

  galleryTitle: {
    ...headingStyle,
    fontSize: fontSizes.h3,
    marginBottom: "24px",
  },

  galleryCarousel: {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
    overflowX: "auto",
    scrollSnapType: "x mandatory",
    padding: "16px",
    backgroundColor: colors.sectionAlt,
    borderRadius: "8px",
    border: `1px solid ${colors.border}`,
  },

  galleryCarouselMobile: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  galleryCard: {
    position: "relative",
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    padding: "24px",
    minWidth: "320px",
    maxWidth: "400px",
    width: "100%",
    flex: "0 0 auto",
    scrollSnapAlign: "start",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  galleryCardActive: {
    backgroundColor: colors.surfaceAlt,
    border: `3px solid ${colors.accentPrimary}`,
  },

  galleryCardTop: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },

  h4: {
    ...headingStyle,
    fontSize: fontSizes.h3,
    margin: 0,
    flex: "1 1 auto",
  },

  galleryTag: {
    backgroundColor: colors.accentPrimary,
    color: colors.white,
    padding: "2px 8px",
    borderRadius: "8px",
    fontSize: fontSizes.small,
    fontFamily: fonts.body,
    fontWeight: 600,
  },

  galleryCardSummary: {
    ...bodyTextStyle,
    fontSize: fontSizes.body,
    color: colors.textPrimary,
    lineHeight: 1.6,
    flex: "1 1 auto",
  },

  viewButton: {
    ...primaryButton,
    width: "100%",
    marginTop: "auto",
  },

  viewButtonDisabled: {
    backgroundColor: colors.border,
    color: colors.textPrimary,
    cursor: "not-allowed",
    opacity: 0.6,
  },
};
